/**
 * THE CRM FICHE OF AN OBJECT — the page a link to an account, a contact or an opportunity opens.
 *
 * One definition (CLAUDE.md, principle 11), read by every surface: the links Otto's tools return
 * (`otto-chat-responses/executors/entity_urls.ts`) and the forms his instructions give him
 * (`instruction_sections.ts`), the Bureau (« Ouvrir dans le CRM », the names in a mission's prose:
 * `src/lib/bureau/crmLinks.ts`), the chat's links (`src/components/ui/Markdown.tsx`) and the CRM's
 * older routes (`src/components/crm/CrmLegacyRoutes.tsx`).
 *
 * An account and a contact open in their page's list, selected by the URL (`?account=`,
 * `?contact=`): the account's tabbed page (`/admin/crm/company/<id>`) stays the deep dive of a LEAD
 * (PLAN-CRM-ACCOUNT-SURFACE), the contact's (`/admin/crm/contacts/<id>`) is « Page complète ». Links
 * written before (conversations, notifications) still carry those forms: `crmFicheOf` reads them,
 * `canonicalCrmHref` sends them to the fiche. Pure and import-free: the admin (Vite) and the Edge
 * functions (Deno) read this same file.
 */

export type CrmFicheKind = 'account' | 'contact' | 'opportunity';

/** The fiche of each kind, with `{id}` where the object's id goes (what `entity_urls` fills in). */
export const CRM_FICHE_TEMPLATE: Readonly<Record<CrmFicheKind, string>> = {
  account: '/admin/crm/accounts?account={id}',
  contact: '/admin/crm/contacts?contact={id}',
  opportunity: '/admin/crm/opportunities/{id}',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/** Whether `value` can be the id of a CRM object (they are all uuids). */
export function isCrmId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID.test(value);
}

/** The fiche of an object. */
export function crmFicheHref(kind: CrmFicheKind, id: string): string {
  return CRM_FICHE_TEMPLATE[kind].replace('{id}', encodeURIComponent(id));
}

/**
 * The object a link of the app opens — its fiche, or one of the older forms — by its id; null for
 * any other link (an absolute URL, a list, a lead's page). `/admin/crm/company/<id>` reads as an
 * account: the page behind it shows a lead when the id is a lead's.
 */
export function crmFicheOf(href: string): { kind: CrmFicheKind; id: string } | null {
  if (!href.startsWith('/admin/crm/')) return null;
  const [path, query = ''] = href.split('#')[0].split('?');
  const [page, id, ...rest] = path.slice('/admin/crm/'.length).split('/');
  if (rest.length > 0) return null;
  const found = (kind: CrmFicheKind, value: string | null | undefined) =>
    isCrmId(value) ? { kind, id: value.toLowerCase() } : null;
  const selected = (key: string) => (id === undefined ? new URLSearchParams(query).get(key) : null);
  switch (page) {
    case 'accounts':
      return found('account', id ?? selected('account'));
    case 'company':
      return found('account', id);
    case 'contacts':
      return found('contact', id ?? selected('contact'));
    case 'opportunities':
      return found('opportunity', id);
    default:
      return null;
  }
}

const OLDER_FICHE = /^\/admin\/crm\/(accounts|contacts)\/([^/?#]+)(?:\?([^#]*))?(#.*)?$/u;

/**
 * A link to an account's or a contact's older page (`/accounts/<id>`, `/contacts/<id>`), sent to
 * its fiche with the rest of its query; any other link unchanged. `/company/<id>` is left to its
 * route, the only one that can tell a lead from an account.
 */
export function canonicalCrmHref(href: string): string {
  const match = OLDER_FICHE.exec(href);
  if (!match || !isCrmId(match[2])) return href;
  const [, page, id, query = '', hash = ''] = match;
  const key = page === 'accounts' ? 'account' : 'contact';
  const rest = [...new URLSearchParams(query)].filter(([name]) => name !== key);
  const params = new URLSearchParams([[key, id.toLowerCase()], ...rest]);
  return `/admin/crm/${page}?${params.toString()}${hash}`;
}
